import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PlayerName from "@/components/PlayerName";
import { PageHeader, Pill } from "@/components/perf";
import { toast } from "sonner";
import { Package, Coins, Crosshair, Loader2, Trophy, Tag, ShoppingCart, X, Layers, Gift, Flame, Store, Send, QrCode, ShieldCheck, ExternalLink, Copy, RefreshCw, Crown, PenLine, HandCoins, Lock } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import ShareMenu from "@/components/ShareMenu";
import MerchantApplyModal from "@/components/MerchantApplyModal";
import AutographModal from "@/components/AutographModal";
import AdBanner from "@/components/AdBanner";
import { zoomCard } from "@/components/CardZoom";
import { SlabShell, CardBackFace } from "@/components/CardParts";
import CardWarsEntry from "@/components/CardWarsEntry";

const RARITY = {
  exclusive: { label: "EXCLUSIVE", cls: "border-pb-red/70 text-pb-red bg-pb-red/10" },
  icon: { label: "ICON", cls: "border-white/70 text-white bg-white/10" },
  legendary: { label: "LEGENDARY", cls: "border-violet-400/70 text-violet-300 bg-violet-400/10" },
  elite: { label: "ELITE", cls: "border-amber-400/70 text-amber-300 bg-amber-400/10" },
  gem: { label: "GEM", cls: "border-sky-400/60 text-sky-300 bg-sky-400/10" },
  gold: { label: "GOLD", cls: "border-yellow-500/70 text-yellow-400 bg-yellow-500/10" },
  holo: { label: "HOLO", cls: "border-fuchsia-400/60 text-fuchsia-300 bg-fuchsia-400/10" },
  rare: { label: "RARE", cls: "border-sky-500/60 text-sky-400 bg-sky-500/10" },
  standard: { label: "STANDARD", cls: "border-zinc-600 text-zinc-400 bg-zinc-800/40" },
  common: { label: "COMMON", cls: "border-stone-500/60 text-stone-400 bg-stone-700/30" },
};

const LOGO_URL = "/brand/pb-logo.png";

const ShowcaseCard = ({ c }) => {
  const [showBack, setShowBack] = useState(false);
  const [back, setBack] = useState(null);
  const [backLoading, setBackLoading] = useState(false);
  const flipCard = async (e) => {
    e.stopPropagation();
    if (!showBack && !back) {
      setBackLoading(true);
      try {
        const { data } = await api.get(`/cards/${c.id}/back`);
        setBack(data);
      } catch { toast.error("Couldn't load the card back — try again"); setBackLoading(false); return; }
      setBackLoading(false);
    }
    setShowBack(!showBack);
  };
  return (
    <div className="bg-pb-surface border border-pb-border p-2 flex flex-col gap-2" data-testid={`showcase-card-${c.id}`}>
      <div className="relative">
        <div className="flip-card">
          <div className={`flip-inner front-sized ${showBack ? "is-flipped" : ""}`}>
            <div className="flip-front bg-black flex items-center justify-center">
              <img src={c.url} alt={c.name} className="w-full aspect-[3/4] object-contain cursor-zoom-in" loading="lazy"
                title="Tap to zoom" onClick={(e) => { e.stopPropagation(); zoomCard(c.url, c.name); }} />
              {c.one_of_one && <span className="absolute top-1 right-1 bg-black border border-amber-400 text-amber-300 text-[9px] font-black uppercase px-1.5 py-0.5">1 of 1</span>}
              {!c.one_of_one && c.edition_total > 0 && <span className="absolute top-1 right-1 bg-black border border-[#A3E635] text-[#A3E635] text-[9px] font-black px-1.5 py-0.5">/{c.edition_total}</span>}
              {c.bounty > 0 && <span className="absolute bottom-1 right-8 bg-pb-red text-black text-[9px] font-black uppercase px-1.5 py-0.5">{c.bounty} BLC bounty</span>}
            </div>
            <div className="flip-back">
              <CardBackFace back={back} testId={`showcase-card-${c.id}`} />
            </div>
          </div>
        </div>
        <span className="absolute top-1 left-1 z-20 bg-black text-white border border-white/80 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 shadow-lg" data-testid={`showcase-card-${c.id}-tag`}>{c.tag}</span>
        <button onClick={flipCard} data-testid={`showcase-card-${c.id}-flip-btn`} title="Flip the card"
          className="absolute bottom-1 right-1 z-30 w-7 h-7 flex items-center justify-center rounded-full bg-black/90 border border-zinc-500 text-zinc-200 hover:border-pb-red hover:text-pb-red transition-colors">
          {backLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
        </button>
      </div>
      <div className="text-xs font-semibold truncate">{c.name}</div>
    </div>
  );
};

const GRADE_STYLES = {
  "BULLPEN BLACK LABEL 10": "border-amber-400 text-amber-300 bg-black",
  "GEM MINT 10": "border-yellow-400/80 text-yellow-300 bg-yellow-500/5",
  "MINT 9.5": "border-zinc-300/60 text-zinc-100 bg-white/5",
  "MINT 9": "border-zinc-500/60 text-zinc-300 bg-transparent",
  "NEAR MINT 8.5": "border-zinc-600/60 text-zinc-400 bg-transparent",
};

const GradeStrip = ({ c, testId }) => c.grade_label ? (
  <div data-testid={`${testId || "card"}-grade`}
    className={`text-center text-[7.5px] font-black uppercase tracking-[0.18em] border px-1 py-[3px] leading-none ${GRADE_STYLES[c.grade_label] || "border-zinc-600 text-zinc-400"}`}>
    Bullpen Grade ◆ {c.grade_label}
  </div>
) : null;

const BRAND_LOGO = "/brand/pb-logo.png";

const GameStatsStrip = ({ g, testId }) => (
  <div data-testid={`${testId}-game-stats`} className="border border-yellow-400/25 bg-black/40 p-1">
    <div className="flex items-center justify-between px-0.5 mb-0.5">
      <span className="text-[6.5px] font-black uppercase tracking-[0.2em] text-yellow-400/80">⚔ Card Wars</span>
      <span className="text-[9px] font-display font-black text-yellow-400" title="Overall battle rating">{g.stats.overall} OVR</span>
    </div>
    <div className="grid grid-cols-5 gap-px">
      {["dink", "power", "speed", "iq", "clutch"].map((s) => (
        <div key={s} className="text-center bg-pb-elevated/70 py-0.5">
          <div className="text-[5.5px] uppercase tracking-wider text-zinc-500">{s.slice(0, 3)}</div>
          <div className={`text-[8px] font-mono font-bold ${s === "clutch" ? "text-yellow-400" : "text-zinc-200"}`}>{g.stats[s]}</div>
        </div>
      ))}
    </div>
    {(g.ability || g.protection_bonus > 0) && (
      <div className="flex items-center justify-between gap-1 mt-0.5 px-0.5">
        {g.ability ? (
          <span className="text-[6.5px] font-black uppercase tracking-widest text-fuchsia-300 truncate" title={g.ability.desc}>
            ⚡ {g.ability.type}
          </span>
        ) : <span />}
        {g.protection_bonus > 0 && (
          <span className={`shrink-0 text-[6.5px] font-black uppercase tracking-widest ${g.black_label ? "text-yellow-300" : "text-teal-300"}`}
            title={`${g.protection_label} — +${g.protection_bonus} battle power`}>
            {g.black_label ? "★ " : ""}{g.protection_label} +{g.protection_bonus}
          </span>
        )}
      </div>
    )}
  </div>
);

const CardTile = ({ c, action, onAction, testId, shareUrl, actions, onBoxIt, selectMode, selected, onToggleSelect }) => {
  const r = RARITY[c.rarity] || RARITY.standard;
  const [showBack, setShowBack] = useState(false);
  const [back, setBack] = useState(null);
  const [backLoading, setBackLoading] = useState(false);
  const flipCard = async (e) => {
    e.stopPropagation();
    if (!showBack && !back && c.card_id) {
      setBackLoading(true);
      try {
        const { data } = await api.get(`/cards/${c.card_id}/back`);
        setBack(data);
      } catch { toast.error("Couldn't load the card back — try again"); setBackLoading(false); return; }
      setBackLoading(false);
    }
    setShowBack(!showBack);
  };
  return (
    <div data-testid={testId} className={`border p-2 flex flex-col gap-2 ${c.one_of_one ? "border-amber-400/70" : c.rarity === "exclusive" ? "border-pb-red/60" : c.rarity === "gem" ? "border-sky-400/40" : c.rarity === "gold" ? "border-yellow-500/50" : c.rarity === "holo" ? "border-fuchsia-400/40" : "border-pb-border"} bg-pb-surface`}>
      <div className="relative">
        <div className="flip-card">
          <div className={`flip-inner front-sized ${showBack ? "is-flipped" : ""}`}>
            <div className="flip-front">
              <SlabShell c={c} testId={testId}>
              <img src={c.url} alt={c.subject_name} className="w-full aspect-[3/4] object-contain bg-black cursor-zoom-in" loading="lazy"
                title="Tap to zoom" onClick={(e) => { e.stopPropagation(); zoomCard(c.url, c.subject_name, c.slabbed ? c : null, c.game); }} />
              {c.one_of_one && (
                <span className="absolute top-1 left-1 bg-black/85 border border-amber-400/70 text-amber-400 text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5" data-testid={`${testId}-ooo-badge`}>
                  1 OF 1
                </span>
              )}
              {c.edition_num && c.edition_total ? (
                <span className="absolute top-1 right-1 bg-black/85 border border-[#A3E635]/70 text-[#A3E635] text-[8px] font-black tracking-widest px-1.5 py-0.5" data-testid={`${testId}-edition-badge`}>
                  {c.edition_num} / {c.edition_total}
                </span>
              ) : null}
              {c.source === "house_reserve" && (
                <span className="absolute bottom-1 left-1 flex items-center gap-1 bg-black/90 border border-yellow-500 text-yellow-400 text-[7px] font-black uppercase tracking-widest px-1.5 py-0.5" data-testid={`${testId}-house-seal`}>
                  <Crown className="w-2.5 h-2.5" /> House Copy
                </span>
              )}
              {c.autographed && (
                <span className="absolute top-6 left-1 flex items-center gap-1 bg-black/90 border border-yellow-400 text-yellow-300 text-[7px] font-black uppercase tracking-widest px-1.5 py-0.5" data-testid={`${testId}-signed-badge`}>
                  <PenLine className="w-2.5 h-2.5" /> Signed
                </span>
              )}
              </SlabShell>
            </div>
            <div className="flip-back">
              <SlabShell c={c} testId={testId}>
                <CardBackFace back={back} copy={c} testId={testId} framed={c.slabbed} />
              </SlabShell>
            </div>
          </div>
        </div>
        {c.card_id && (
          <button onClick={flipCard} data-testid={`${testId}-flip-btn`} title="Flip the card"
            className="absolute bottom-1 right-1 z-30 w-6 h-6 flex items-center justify-center rounded-full bg-black/80 border border-zinc-600 text-zinc-300 hover:border-pb-red hover:text-pb-red transition-colors">
            {backLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          </button>
        )}
        {onBoxIt && !selectMode && (
          <button onClick={(e) => { e.stopPropagation(); onBoxIt(c); }} data-testid={`${testId}-boxit-btn`}
            title="Send to my Safety Deposit Box — hidden from everyone until you take it out"
            className="absolute top-1 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 rounded-full bg-black/85 border border-yellow-600/60 text-yellow-400 text-[7px] font-black uppercase tracking-widest pl-1.5 pr-2 py-1 hover:border-yellow-300 hover:text-yellow-200 hover:shadow-[0_0_10px_rgba(253,224,71,.35)] transition-all active:scale-95">
            <Lock className="w-2.5 h-2.5" /> Box It
          </button>
        )}
        {selectMode && (
          <button onClick={(e) => { e.stopPropagation(); onToggleSelect(c); }} data-testid={`${testId}-select-overlay`}
            className={`absolute inset-0 z-40 transition-colors ${selected ? "bg-yellow-400/15 border-2 border-yellow-400" : "bg-black/30 border-2 border-transparent hover:border-yellow-500/50"}`}>
            <span className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center border text-[10px] font-black ${selected ? "bg-yellow-400 text-black border-yellow-400" : "bg-black/70 text-zinc-500 border-zinc-500"}`}>
              {selected ? "✓" : ""}
            </span>
          </button>
        )}
      </div>
      {!c.slabbed && <GradeStrip c={c} testId={testId} />}
      {c.game && <GameStatsStrip g={c.game} testId={testId} />}
      {!c.slabbed && c.protection && (
        <span className="self-start text-[7px] font-black uppercase tracking-widest px-1.5 py-0.5 border border-teal-400/60 text-teal-300 bg-teal-400/10" data-testid={`${testId}-protection`}>
          {c.protection === "toploader" ? (c.grade_locked ? "🛡 Top Loaded · Grade Locked" : "🛡 Top Loaded") : "🛡 Sleeved"}
        </span>
      )}
      <div className="flex items-center justify-between gap-1">
        <span className="flex items-center gap-1 min-w-0">
          {c.rc && <span className="shrink-0 text-[7px] font-black px-1 py-0.5 bg-red-600 text-white" title="Rookie Card">RC</span>}
          <span className="text-[11px] font-semibold truncate">{c.subject_name}</span>
        </span>
        <span className={`shrink-0 text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5 border ${r.cls}`}>
          {c.rarity === "gem" && c.gem_label ? c.gem_label.split(" ")[0] : r.label}
        </span>
      </div>
      <div className="flex items-center justify-between gap-1">
        <span className="flex items-center gap-1">
          {c.hot_multiplier > 1 && <span className="flex items-center gap-0.5 text-[9px] font-black text-orange-400" title="Hot Card multiplier"><Flame className="w-3 h-3" />{c.hot_multiplier}x</span>}
          {c.bounty_won > 0 && <span className="text-[9px] font-black text-pb-red" title="Won a bounty">+{c.bounty_won} BLC</span>}
        </span>
        {c.price > 0 && <span className="text-[10px] font-mono font-bold text-pb-cyan">{c.price} BLC</span>}
      </div>
      {action && (
        <button onClick={() => onAction(c)} data-testid={`${testId}-action`}
          className="w-full text-[9px] font-black uppercase tracking-widest py-1.5 bg-pb-elevated border border-pb-border hover:border-pb-red hover:text-pb-red transition-colors">
          {action}
        </button>
      )}
      {actions && (
        <div className="grid grid-cols-2 gap-1">
          {actions.map((a) => (
            <button key={a.tid} onClick={() => a.onClick(c)} data-testid={`${testId}-${a.tid}`}
              className="text-[9px] font-black uppercase tracking-widest py-1.5 bg-pb-elevated border border-pb-border hover:border-pb-cyan hover:text-pb-cyan transition-colors">
              {a.label}
            </button>
          ))}
        </div>
      )}
      {shareUrl && c.one_of_one && (
        <div className="flex justify-center border border-amber-400/40 bg-amber-400/10 py-1.5" data-testid={`${testId}-grail-share`}>
          <ShareMenu url={shareUrl} image={c.url}
            text={`I own the ONLY one 🏆 My 1-of-1 "${c.subject_name}" card from the Pickle Bullpen Card Vault — there will never be another.`}
            testId={`${testId}-grail-share-menu`} label="I own the only one" />
        </div>
      )}
    </div>
  );
};

function SlabCeremony({ c, fee, onClose, onDone }) {
  const [stage, setStage] = useState("confirm");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const seal = async () => {
    setStage("sealing");
    try {
      const { data } = await api.post(`/vault/copies/${c.id}/slab`);
      setTimeout(() => { setResult(data); setStage("reveal"); }, 2400);
    } catch (e) {
      setErr(e.response?.data?.detail || "Grading failed — you were not charged");
      setStage("confirm");
    }
  };
  return (
    <div className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center p-4" data-testid="slab-ceremony">
      <div className="max-w-sm w-full text-center">
        {stage === "confirm" && (
          <>
            <div className="text-[10px] uppercase tracking-[0.35em] text-pb-red font-black mb-4">Bullpen Grading</div>
            <img src={c.url} alt="" className="w-40 mx-auto aspect-[3/4] object-contain bg-black border border-pb-border mb-4" />
            <p className="text-sm text-zinc-300 mb-1 font-semibold">"{c.subject_name}"{c.autographed ? " — SIGNED" : ""}</p>
            <p className="text-xs text-zinc-500 mb-4">Law of the Vault: only SIGNED cards ride to grading. It comes back sealed for life in an official Bullpen slab with a gold autograph label — top loaded cards keep their locked grade, giving a signed perfect 10 a clean shot at BLACK LABEL.</p>
            {err && <p className="text-xs text-red-400 mb-3" data-testid="slab-error">{err}</p>}
            <button data-testid="slab-confirm-btn" onClick={seal}
              className="w-full bg-pb-red text-black font-black uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-95 transition-all">
              Seal It — {fee} BLC
            </button>
            <button data-testid="slab-cancel-btn" onClick={() => onClose(false)} className="mt-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white">Not yet</button>
          </>
        )}
        {stage === "sealing" && (
          <div className="relative h-96 flex items-center justify-center" data-testid="slab-sealing">
            <div className="slab-shell absolute w-56 h-80 border-4 border-zinc-300/50 bg-white/[0.04]" />
            <img src={c.url} alt="" className="slab-inserting w-44 aspect-[3/4] object-contain bg-black" />
            <div className="absolute bottom-0 left-0 right-0 text-[10px] uppercase tracking-[0.35em] text-zinc-400 font-bold animate-pulse">Encapsulating…</div>
          </div>
        )}
        {stage === "reveal" && result && (
          <div className="slab-reveal" data-testid="slab-reveal">
            <div className="mx-auto w-60 slab-case p-2 slab-shine relative">
              <span className="slab-weld" style={{ top: 4, left: 4 }} />
              <span className="slab-weld" style={{ top: 4, right: 4 }} />
              <span className="slab-weld" style={{ bottom: 4, left: 4 }} />
              <span className="slab-weld" style={{ bottom: 4, right: 4 }} />
              <div className={`slab-stamp flex items-center gap-2 border px-2 py-1.5 mb-2 ${result.signed
                ? "bg-gradient-to-r from-amber-500/30 to-yellow-500/20 border-amber-400" : "bg-white/10 border-zinc-300/50"}`}>
                <img src={BRAND_LOGO} alt="" className="w-8 h-8 object-contain" />
                <div className="flex-1 text-left leading-tight">
                  <div className="text-[9px] font-black uppercase tracking-widest text-white">{c.subject_name}{result.signed ? " · AUTOGRAPH" : ""}</div>
                  <div className="text-[8px] font-mono text-zinc-400">CERT {result.cert}</div>
                </div>
                <div className="text-right leading-none">
                  <div className={`text-2xl font-black ${result.grade_label === "BULLPEN BLACK LABEL 10" ? "text-amber-300" : "text-white"}`}>{result.grade}</div>
                </div>
              </div>
              <div className="slab-window"><img src={c.url} alt="" className="w-full aspect-[3/4] object-contain bg-black" /></div>
            </div>
            <div className="mt-4 text-xl font-display font-black uppercase text-pb-red slab-stamp" data-testid="slab-grade-reveal">{result.grade_label}</div>
            {result.signed && <div className="text-xs text-amber-300 font-bold uppercase tracking-widest mt-1">Gold Autograph Label 🖊️</div>}
            <p className="text-xs text-zinc-500 mt-2">Sealed for life · announced to the community</p>
            <button data-testid="slab-done-btn" onClick={() => onClose(true)}
              className="mt-4 w-full bg-pb-red text-black font-black uppercase tracking-widest py-3 hover:bg-[#BEF264] active:scale-95 transition-all">
              Back to My Vault
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Vault() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [mine, setMine] = useState(null);
  const [bounties, setBounties] = useState([]);
  const [market, setMarket] = useState([]);
  const [marketPage, setMarketPage] = useState(0);
  const [mkRarity, setMkRarity] = useState("all");
  const [mkGrade, setMkGrade] = useState("all");
  const [mkMin, setMkMin] = useState("");
  const [mkMax, setMkMax] = useState("");
  const [selMode, setSelMode] = useState(false);
  const [selIds, setSelIds] = useState([]);
  const [lb, setLb] = useState(null);
  const [tab, setTab] = useState("collection");
  const [showcase, setShowcase] = useState([]);
  const [showcaseOpen, setShowcaseOpen] = useState(false);
  const [pool, setPool] = useState(null);
  const [opening, setOpening] = useState(false);
  const [packResult, setPackResult] = useState(null);
  const [flipped, setFlipped] = useState({});
  const [flash, setFlash] = useState(false);
  const [listing, setListing] = useState(null);
  const [merchantOpen, setMerchantOpen] = useState(false);
  const [listPrice, setListPrice] = useState("");
  const [busy, setBusy] = useState(false);
  const [gifting, setGifting] = useState(null);
  const [signable, setSignable] = useState({});
  const [signing, setSigning] = useState(null);
  const [recipQ, setRecipQ] = useState("");
  const [recips, setRecips] = useState([]);
  const [recip, setRecip] = useState(null);
  const [giftFee, setGiftFee] = useState(0);
  const [giftBusy, setGiftBusy] = useState(false);
  const [qrCard, setQrCard] = useState(null);
  const [registry, setRegistry] = useState(null);
  const [slabbing, setSlabbing] = useState(null);
  const [offers, setOffers] = useState(null);

  const load = useCallback(() => {
    api.get("/vault/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/vault/mine").then(({ data }) => setMine(data)).catch(() => {});
    api.get("/vault/bounties").then(({ data }) => setBounties(data)).catch(() => {});
    api.get("/vault/market").then(({ data }) => setMarket(data)).catch(() => {});
    api.get("/vault/leaderboard").then(({ data }) => setLb(data)).catch(() => {});
    api.get("/vault/transfer-fee").then(({ data }) => setGiftFee(data.fee_coins)).catch(() => {});
    api.get("/vault/showcase").then(({ data }) => setShowcase(data)).catch(() => {});
    api.get("/autograph/signable").then(({ data }) => setSignable(data)).catch(() => {});
    api.get("/vault/offers").then(({ data }) => setOffers(data)).catch(() => {});
  }, []);
  useEffect(load, [load]);
  useEffect(() => {
    if (!gifting || recipQ.trim().length < 2) { setRecips([]); return; }
    const t = setTimeout(() => {
      api.get(`/vault/gift/recipients?q=${encodeURIComponent(recipQ.trim())}`).then(({ data }) => setRecips(data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [recipQ, gifting]);
  useEffect(() => {
    if (tab === "pool") api.get("/vault/pool").then(({ data }) => setPool(data)).catch(() => {});
  }, [tab]);

  const openPack = async (pack) => {
    setOpening(pack);
    try {
      const { data } = await api.post("/vault/packs/open", { pack }, { timeout: 60000 });
      setFlipped({});
      setPackResult(data);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOpening(false); }
  };

  const submitList = async () => {
    setBusy(true);
    try {
      await api.post(`/vault/copies/${listing.id}/list`, { price: Number(listPrice) || 0 });
      toast.success(Number(listPrice) > 0 ? `Listed for ${listPrice} BLC` : "Removed from the marketplace");
      setListing(null); setListPrice("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const buy = async (c) => {
    if (!window.confirm(`Buy ${c.subject_name}'s card from ${c.owner_name} for ${c.price} BLC?`)) return;
    try {
      await api.post(`/vault/market/${c.id}/buy`);
      toast.success(`${c.subject_name}'s card is now in your vault!`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const flip = (i) => {
    setFlipped((f) => {
      if (f[i]) return f;
      if (packResult?.cards[i]?.bounty_won > 0) {
        setFlash(true);
        setTimeout(() => setFlash(false), 900);
      }
      return { ...f, [i]: true };
    });
  };
  const flipAll = () => packResult?.cards.forEach((_, i) => setTimeout(() => flip(i), i * 380));

  const sendGift = async () => {
    setGiftBusy(true);
    try {
      const { data } = await api.post(`/vault/cards/${gifting.id}/gift`, { to_user_id: recip.id });
      toast.success(`Card sent to ${data.to_name}! Serial ${data.serial}`);
      setGifting(null); setRecip(null); setRecipQ(""); setRecips([]);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGiftBusy(false); }
  };

  const openQr = async (c) => {
    setQrCard(c); setRegistry(null);
    try {
      const { data } = await api.get(`/vault/copies/${c.id}/registry`);
      setRegistry(data);
      if (data.url && data.url !== c.url) load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setQrCard(null); }
  };

  const claimSet = async (s) => {
    try {
      const { data } = await api.post(`/vault/sets/${s.id}/claim`);
      toast.success(`Set complete! +${data.reward} bonus BLC`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const respondOffer = async (o, accept) => {
    if (accept && !window.confirm(`Accept ${o.buyer_name}'s ${o.amount} BLC offer for ${o.subject_name}'s card? Standard marketplace fee & royalties apply.`)) return;
    try {
      const { data } = await api.post(`/vault/offers/${o.id}/respond`, { accept });
      toast.success(accept ? `Sold! ${data.seller_take} BLC added to your wallet` : "Offer declined");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const boxIt = async (c) => {
    if (!window.confirm(`Send ${c.subject_name}'s card to your Safety Deposit Box? It'll be hidden from your collection, the market and Card Wars until you take it out.${c.price > 0 ? " Its marketplace listing will be removed." : ""}`)) return;
    try {
      await api.post(`/depositbox/cards/${c.id}`);
      toast.success(`${c.subject_name}'s card is locked in your deposit box 🔒`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleSel = (c) => setSelIds((s) => s.includes(c.id) ? s.filter((x) => x !== c.id) : [...s, c.id]);
  const bulkBox = async () => {
    if (!selIds.length) return;
    if (!window.confirm(`Send ${selIds.length} card${selIds.length === 1 ? "" : "s"} to your Safety Deposit Box? They'll be hidden from everyone and any listings removed.`)) return;
    try {
      const { data } = await api.post("/depositbox/cards/bulk", { copy_ids: selIds });
      toast.success(`${data.boxed} card${data.boxed === 1 ? "" : "s"} locked in your deposit box 🔒`);
      setSelMode(false); setSelIds([]);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const protect = async (c, type) => {
    const fee = type === "sleeve" ? (cfg?.sleeve_fee_blc ?? 1) : (cfg?.toploader_fee_blc ?? 3);
    const label = type === "sleeve" ? "Penny Sleeve" : "Top Loader";
    const perk = type === "sleeve"
      ? "Slows aging by half · +2 battle power."
      : "Stops aging completely, locks a PERMANENT grade, +4 battle power. Law of the Vault: this is a card's final stop until it gets signed — then slabbing unlocks.";
    if (!window.confirm(`${label} for ${fee} BLC? ${perk}`)) return;
    try {
      const { data } = await api.post(`/vault/copies/${c.id}/protect`, { type });
      toast.success(data.grade_locked
        ? `${label} applied — permanent grade locked: ${data.grade_locked.grade_label}`
        : `${label} applied — this card just got tougher`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!cfg || !mine) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-pb-cyan animate-spin" /></div>;

  if (!cfg.enabled && user?.role !== "admin") {
    return (
      <div data-testid="vault-closed" className="text-center py-24">
        <Layers className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
        <div className="font-display font-black text-2xl uppercase mb-2">The Card Vault opens soon</div>
        <p className="text-sm text-zinc-500">Collect other players' AI sports cards, hunt BLC bounties and trade on the marketplace.</p>
      </div>
    );
  }

  const myListings = mine.copies.filter((c) => c.price > 0).length;

  return (
    <div data-testid="vault-page" className="relative">
      <div className="fixed inset-0 z-0 pointer-events-none" data-testid="vault-bank-bg">
        <img src="/sdb/bank-lobby.jpg" alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${tab === "market" ? "opacity-0" : "opacity-100"}`} />
        <img src="/sdb/card-store.jpg" alt="" data-testid="vault-market-bg" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${tab === "market" ? "opacity-100" : "opacity-0"}`} />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-transparent to-black/85" />
      </div>
      <div className="relative z-[1]">
      <PageHeader kicker="Collect · Hunt Bounties · Trade" title="The Card Vault"
        subtitle="Rip BLC packs to collect your rivals' AI sports cards, hit hidden bounty cards for instant coin prizes, complete sets, and flip your doubles on the marketplace." />
      <a href="/grails" data-testid="vault-grails-link"
        className="inline-flex items-center gap-2 border border-amber-400/50 bg-amber-400/10 text-amber-400 text-[10px] font-black uppercase tracking-widest px-4 py-2.5 mb-6 mr-3 hover:bg-amber-400/20 transition-colors">
        🏆 Grail Gallery — every one-of-one ever pulled
      </a>
      <button data-testid="vault-card-merchant-btn" onClick={() => setMerchantOpen(true)}
        className="inline-flex items-center gap-2 border border-pb-cyan/50 bg-pb-cyan/10 text-pb-cyan text-[10px] font-black uppercase tracking-widest px-4 py-2.5 mb-6 hover:bg-pb-cyan/20 transition-colors">
        <Store className="w-3.5 h-3.5" /> Become a Card Merchant — get paid for card sales
      </button>
      <MerchantApplyModal open={merchantOpen} onClose={() => setMerchantOpen(false)} presetType="card_merchant" />
      <AdBanner zone="vault" />

      {/* Showcase Window */}
      {showcase.length > 0 && (
        <div className="relative border border-[#A3E635]/40 bg-gradient-to-r from-[#0f1408] via-pb-surface to-pb-surface p-5 mb-8 overflow-hidden" data-testid="showcase-banner">
          <div className="flex flex-wrap items-center gap-5">
            <div className="flex -space-x-6 shrink-0">
              {showcase.slice(0, 4).map((c, i) => (
                <img key={c.id} src={c.url} alt="" loading="lazy"
                  className="w-14 aspect-[3/4] object-contain bg-black border border-zinc-700 shadow-xl cursor-zoom-in"
                  onClick={() => zoomCard(c.url, c.name)}
                  style={{ transform: `rotate(${(i - 1.5) * 6}deg)`, zIndex: 10 - i }} />
              ))}
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#A3E635] mb-0.5">Curated by the Bullpen</div>
              <div className="font-display font-black uppercase text-xl leading-tight">The Showcase Window</div>
              <p className="text-xs text-zinc-500 mt-0.5">{showcase.length} card{showcase.length === 1 ? "" : "s"} on display — sneak peeks, chase cards & what's dropping next. Look before everyone else does.</p>
            </div>
            <button data-testid="showcase-open-btn" onClick={() => setShowcaseOpen(true)}
              className="shrink-0 flex items-center gap-2 bg-[#A3E635] text-black font-black uppercase tracking-widest px-6 py-3 text-xs hover:bg-[#BEF264] active:scale-95 transition-all">
              <Layers className="w-4 h-4" /> Window Shop
            </button>
          </div>
        </div>
      )}

      {/* Showcase gallery modal moved below (outside bg z-wrapper) */}
      {!cfg.enabled && <div className="border border-amber-400/50 bg-amber-400/10 text-amber-300 text-xs font-bold px-4 py-2.5 mb-6" data-testid="vault-admin-preview">Admin preview — the Vault is currently OFF for members. Launch it from the Admin tab.</div>}

      {/* Packs */}
      <div className="grid sm:grid-cols-2 gap-4 mb-8" data-testid="vault-packs">
        {[["small", "Starter Pack"], ["large", "Rip Pack"]].map(([k, name]) => (
          <div key={k} className="bg-pb-surface border border-pb-border p-5 flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 font-display font-bold text-lg uppercase"><Package className="w-4 h-4 text-pb-red" /> {name}</div>
              <div className="text-xs text-zinc-500 mt-1">{cfg.packs[k].cards} random player cards per pack</div>
              <div className="flex flex-wrap items-center gap-1.5 mt-2" data-testid={`vault-live-odds-${k}`}>
                <span className="text-[8px] uppercase tracking-[0.2em] text-zinc-600 font-bold">Live odds</span>
                {[["exclusive", "Exclusive", "border-pb-red/60 text-pb-red"],
                  ["icon", "Icon", "border-white/60 text-white"],
                  ["legendary", "Legendary", "border-violet-400/60 text-violet-300"],
                  ["elite", "Elite", "border-amber-400/60 text-amber-300"],
                  ["gold", "Gold", "border-yellow-500/60 text-yellow-400"],
                  ["holo", "Holo", "border-fuchsia-400/60 text-fuchsia-300"],
                  ["rare", "Rare", "border-sky-400/60 text-sky-300"],
                  ["common", "Common", "border-stone-400/60 text-stone-300"]]
                  .filter(([tier]) => Number(cfg.odds[tier]) > 0)
                  .map(([tier, label, cls]) => (
                    <span key={tier} data-testid={`live-odds-${k}-${tier}`}
                      className={`border px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ${cls}`}>
                      {label} {Number(cfg.odds[tier])}%
                    </span>
                  ))}
              </div>
            </div>
            <button data-testid={`vault-open-${k}`} onClick={() => openPack(k)} disabled={!!opening}
              className="shrink-0 flex items-center gap-2 bg-pb-red text-black font-black uppercase tracking-widest px-5 py-3 text-xs hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-50">
              {opening === k ? <Loader2 className="w-4 h-4 animate-spin" /> : <Coins className="w-4 h-4" />} {cfg.packs[k].price} BLC
            </button>
          </div>
        ))}
      </div>

      {/* Bounty board */}
      {bounties.length > 0 && (
        <div className="border border-pb-red/40 bg-pb-red/5 p-5 mb-8" data-testid="vault-bounty-board">
          <div className="flex items-center gap-2 mb-1"><Crosshair className="w-4 h-4 text-pb-red" /><span className="font-display font-bold text-lg uppercase">Active Bounties</span></div>
          <p className="text-xs text-zinc-500 mb-4">Pull one of these cards from a pack and the BLC prize is yours — instantly.</p>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {bounties.map((b) => (
              <div key={b.card_id} data-testid={`bounty-${b.card_id}`} className="shrink-0 w-32 border border-pb-red/40 bg-pb-surface p-2 text-center">
                <img src={b.url} alt={b.subject_name} className="w-full aspect-[3/4] object-contain bg-black mb-2 cursor-zoom-in"
                  title="Tap to zoom" onClick={() => zoomCard(b.url, b.subject_name)} />
                <div className="text-[10px] font-semibold truncate">{b.subject_name}</div>
                <div className="font-display font-black text-pb-red">{b.prize} BLC</div>
                {b.perks && (
                  <div className="flex items-start justify-center gap-1 text-[8px] font-bold text-amber-300 leading-tight mt-0.5" data-testid={`bounty-perks-${b.card_id}`}>
                    <Gift className="w-2.5 h-2.5 shrink-0 mt-[1px]" /><span>+ {b.perks}</span>
                  </div>
                )}
                <div className="text-[9px] text-zinc-500 uppercase tracking-widest">{b.copies_left} left</div>
                {b.subject_id === user?.id && <div className="text-[8px] uppercase tracking-widest text-amber-400 mt-0.5">Your card — others chase it</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sets */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="vault-sets">
        {mine.sets.map((s) => (
          <div key={s.id} className={`border p-4 ${s.claimed ? "border-emerald-500/40 bg-emerald-500/5" : "border-pb-border bg-pb-surface"}`} data-testid={`vault-set-${s.id}`}>
            <div className="flex items-center gap-1.5 text-sm font-bold"><Gift className="w-3.5 h-3.5 text-pb-cyan" /> {s.name}
              {s.badge && <span className={`ml-auto text-[9px] font-black px-1.5 py-0.5 border ${s.claimed ? "border-amber-400 text-amber-300 bg-amber-400/10" : "border-zinc-700 text-zinc-600"}`} title={s.claimed ? "Badge earned!" : "Complete the set to earn this badge"} data-testid={`set-badge-${s.id}`}>{s.badge}</span>}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">{s.desc}</div>
            <div className="h-1.5 bg-pb-elevated mt-2 mb-2"><div className="h-full bg-pb-cyan transition-all" style={{ width: `${(s.have / s.need) * 100}%` }} /></div>
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono text-zinc-400">{s.have}/{s.need}</span>
              {s.claimed ? <span className="text-[9px] font-black uppercase text-emerald-400">Claimed</span>
                : s.complete ? <button data-testid={`claim-set-${s.id}`} onClick={() => claimSet(s)} className="text-[9px] font-black uppercase tracking-widest bg-pb-cyan text-black px-2.5 py-1 hover:bg-[#BEF264]">Claim +{s.reward} BLC</button>
                : <span className="text-[9px] text-zinc-600 uppercase">+{s.reward} bonus BLC</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Leaderboards */}
      {lb && lb.collectors.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="vault-leaderboard">
          {[["Top Collectors", lb.collectors, (r) => `${r.copies} cards`, (r) => `${r.unique_cards} unique`, "collectors"],
            ["Bounty Hunters", lb.bounty_hunters, (r) => `+${r.bounty_blc} BLC`, (r) => `${r.bounty_hits} hit${r.bounty_hits !== 1 ? "s" : ""}`, "hunters"]].map(([title, rows, stat, sub, tid]) => (
            <div key={tid} className="bg-pb-surface border border-pb-border p-5" data-testid={`vault-lb-${tid}`}>
              <div className="flex items-center gap-2 mb-3">
                <Trophy className="w-4 h-4 text-pb-red" />
                <span className="font-display font-bold uppercase">{title}</span>
              </div>
              {rows.length === 0 ? (
                <div className="text-xs text-zinc-600">No bounties hit yet — they're hiding in packs.</div>
              ) : (
                <div className="space-y-1.5">
                  {rows.slice(0, 5).map((r, i) => (
                    <div key={r.user_id} className={`flex items-center gap-3 px-3 py-2 ${r.user_id === user?.id ? "border border-pb-red/40 bg-pb-red/5" : "bg-pb-elevated/50"}`}
                      data-testid={`vault-lb-${tid}-row-${i}`}>
                      <span className={`w-5 text-center font-display font-black ${i === 0 ? "text-yellow-400" : i === 1 ? "text-zinc-300" : i === 2 ? "text-amber-600" : "text-zinc-600"}`}>{i + 1}</span>
                      <span className="flex-1 text-sm font-semibold truncate"><PlayerName userId={r.user_id} name={r.name || "Player"} />{r.user_id === user?.id && <span className="text-[9px] text-pb-red uppercase ml-1.5">You</span>}</span>
                      <span className="text-xs font-mono font-bold text-pb-cyan">{stat(r)}</span>
                      <span className="text-[9px] uppercase tracking-widest text-zinc-500 w-14 text-right">{sub(r)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Collection / Market tabs */}
      <div className="flex gap-2 mb-4 flex-wrap">
        <button data-testid="vault-tab-collection" onClick={() => setTab("collection")}
          className={`px-5 py-2.5 text-xs font-black uppercase tracking-widest border ${tab === "collection" ? "bg-pb-red text-black border-pb-red" : "border-pb-border text-zinc-400 hover:border-pb-red"}`}>
          My Collection ({mine.copies.length})
        </button>
        <button data-testid="vault-tab-market" onClick={() => setTab("market")}
          className={`px-5 py-2.5 text-xs font-black uppercase tracking-widest border ${tab === "market" ? "bg-pb-cyan text-black border-pb-cyan" : "border-pb-border text-zinc-400 hover:border-pb-cyan"}`}>
          Marketplace ({market.length})
        </button>
        <button data-testid="vault-tab-pool" onClick={() => setTab("pool")}
          className={`px-5 py-2.5 text-xs font-black uppercase tracking-widest border ${tab === "pool" ? "bg-[#A3E635] text-black border-[#A3E635]" : "border-pb-border text-zinc-400 hover:border-[#A3E635]"}`}>
          Card Pool{pool ? ` (${pool.total})` : ""}
        </button>
        {myListings > 0 && <Pill color="#22D3EE">{myListings} of yours listed</Pill>}
      </div>

      {tab === "collection" && <CardWarsEntry testId="cardwars-entry-vault" />}

      {tab === "collection" && (offers?.incoming?.length > 0 || offers?.outgoing?.length > 0) && (
        <div className="border border-amber-400/40 bg-amber-400/5 p-4 mb-4" data-testid="vault-offers-panel">
          <div className="flex items-center gap-2 mb-1">
            <HandCoins className="w-4 h-4 text-amber-400" />
            <span className="font-display font-bold uppercase text-sm">Card Offers</span>
          </div>
          {offers.incoming.map((o) => (
            <div key={o.id} className="flex items-center gap-3 py-2 border-t border-amber-400/15" data-testid={`offer-row-${o.id}`}>
              {o.card_url && <img src={o.card_url} alt="" className="w-8 h-11 object-cover border border-pb-border shrink-0" />}
              <div className="flex-1 min-w-0 text-xs text-zinc-300">
                <b className="text-white">{o.buyer_name}</b> offered <b className="text-pb-cyan font-mono">{o.amount} BLC</b> for {o.subject_name}'s card{o.grade_label ? ` (${o.grade_label})` : ""}
              </div>
              <button data-testid={`offer-accept-${o.id}`} onClick={() => respondOffer(o, true)}
                className="shrink-0 text-[9px] font-black uppercase tracking-widest px-3 py-1.5 bg-pb-red text-black hover:bg-[#BEF264] active:scale-95 transition-all">Accept</button>
              <button data-testid={`offer-decline-${o.id}`} onClick={() => respondOffer(o, false)}
                className="shrink-0 text-[9px] font-black uppercase tracking-widest px-3 py-1.5 border border-pb-border text-zinc-400 hover:border-red-500 hover:text-red-400 transition-colors">Decline</button>
            </div>
          ))}
          {offers.outgoing.map((o) => (
            <div key={o.id} className="flex items-center gap-3 py-2 border-t border-amber-400/15 opacity-80" data-testid={`offer-out-row-${o.id}`}>
              {o.card_url && <img src={o.card_url} alt="" className="w-8 h-11 object-cover border border-pb-border shrink-0" />}
              <div className="flex-1 min-w-0 text-xs text-zinc-400">
                You offered <b className="text-pb-cyan font-mono">{o.amount} BLC</b> for {o.subject_name}'s card — waiting on {o.owner_name}
              </div>
              <span className="shrink-0 text-[8px] font-black uppercase tracking-widest text-amber-400 border border-amber-400/40 px-2 py-1">Pending</span>
            </div>
          ))}
        </div>
      )}

      {tab === "collection" ? (
        mine.copies.length === 0 ? (
          <div className="text-sm text-zinc-500 border border-pb-border bg-pb-surface p-8 text-center" data-testid="vault-empty">
            Your vault is empty — rip your first pack above to start collecting.
          </div>
        ) : (
          <>
          <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="collection-toolbar">
            {!selMode ? (
              <button data-testid="bulk-select-btn" onClick={() => { setSelMode(true); setSelIds([]); }}
                className="flex items-center gap-1.5 border border-yellow-600/60 text-yellow-400 text-[10px] font-black uppercase tracking-widest px-3.5 py-2 hover:bg-yellow-500/10 transition-colors">
                <Lock className="w-3 h-3" /> Select to Box
              </button>
            ) : (
              <>
                <button data-testid="bulk-box-btn" onClick={bulkBox} disabled={!selIds.length}
                  className="flex items-center gap-1.5 bg-yellow-400 text-black text-[10px] font-black uppercase tracking-widest px-4 py-2 hover:bg-yellow-300 active:scale-95 transition-all disabled:opacity-40">
                  <Lock className="w-3 h-3" /> Box {selIds.length || ""} Card{selIds.length === 1 ? "" : "s"}
                </button>
                <button data-testid="bulk-select-all" onClick={() => setSelIds(mine.copies.map((c) => c.id))}
                  className="text-[10px] font-black uppercase tracking-widest border border-pb-border text-zinc-400 px-3 py-2 hover:border-yellow-500/60 hover:text-yellow-400 transition-colors">
                  Select All
                </button>
                <button data-testid="bulk-cancel" onClick={() => { setSelMode(false); setSelIds([]); }}
                  className="text-[10px] font-black uppercase tracking-widest border border-pb-border text-zinc-500 px-3 py-2 hover:border-white hover:text-white transition-colors">
                  Cancel
                </button>
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">tap cards to pick them</span>
              </>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="vault-collection">
            {mine.copies.map((c) => (
              <CardTile key={c.id} c={c} testId={`copy-${c.id}`}
                onBoxIt={boxIt}
                selectMode={selMode} selected={selIds.includes(c.id)} onToggleSelect={toggleSel}
                shareUrl={c.one_of_one ? `${process.env.REACT_APP_BACKEND_URL}/api/share/grails/${c.id}` : null}
                action={c.price > 0 ? "Edit Listing" : "Sell"} onAction={(x) => { setListing(x); setListPrice(x.price || ""); }}
                actions={[
                  { tid: "gift", label: "Send", onClick: (x) => { setGifting(x); setRecip(null); setRecipQ(""); setRecips([]); } },
                  { tid: "qr", label: "Verify QR", onClick: openQr },
                  ...(signable[c.card_id] && !c.autographed && !c.slabbed
                    ? [{ tid: "sign", label: "✒ Get Signed", onClick: (x) => setSigning(x) }] : []),
                  ...(!c.slabbed && c.autographed
                    ? [{ tid: "slab", label: "Slab & Grade", onClick: (x) => setSlabbing(x) }] : []),
                  ...(!c.slabbed && !c.protection
                    ? [{ tid: "sleeve", label: `Sleeve (${cfg?.sleeve_fee_blc ?? 1} BLC)`, onClick: (x) => protect(x, "sleeve") }] : []),
                  ...(!c.slabbed && c.protection !== "toploader"
                    ? [{ tid: "toploader", label: `Top Loader (${cfg?.toploader_fee_blc ?? 3} BLC)`, onClick: (x) => protect(x, "toploader") }] : []),
                ]} />
            ))}
          </div>
          </>
        )
      ) : tab === "pool" ? (
        !pool ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#A3E635] animate-spin" /></div>
        ) : pool.total === 0 ? (
          <div className="text-sm text-zinc-500 border border-pb-border bg-pb-surface p-8 text-center" data-testid="pool-empty">
            The pool is empty right now — as more players mint cards, they'll show up here.
          </div>
        ) : (
          <div data-testid="vault-pool">
            <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="pool-stats">
              <span className="border border-[#A3E635]/50 bg-[#A3E635]/10 text-[#A3E635] text-[10px] font-black uppercase tracking-widest px-3 py-1.5">
                {pool.total} card{pool.total === 1 ? "" : "s"} in the pool
              </span>
              {["exclusive", "icon", "legendary", "elite", "gold", "holo", "rare", "gem", "common", "standard"].map((r) => (pool.counts[r] ? (
                <span key={r} data-testid={`pool-count-${r}`} className={`text-[10px] font-bold uppercase tracking-widest px-2.5 py-1.5 border ${(RARITY[r] || RARITY.standard).cls}`}>
                  {pool.counts[r]} {r}{pool.odds?.[r] != null ? ` · ${pool.odds[r]}%` : ""}
                </span>
              ) : null))}
            </div>
            <p className="text-xs text-zinc-500 mb-4">Every card below is hiding in packs right now — rip one and it could be yours.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="pool-grid">
              {pool.cards.map((c, i) => (
                <div key={i} data-testid={`pool-card-${i}`}
                  className={`relative border p-2 bg-pb-surface ${c.one_of_one ? "border-amber-400/70" : c.rarity === "exclusive" ? "border-pb-red/60" : c.rarity === "icon" ? "border-white/50" : c.rarity === "legendary" ? "border-violet-400/50" : c.rarity === "gold" ? "border-yellow-500/50" : c.rarity === "holo" ? "border-fuchsia-400/40" : c.rarity === "gem" ? "border-sky-400/40" : "border-pb-border"}`}>
                  <div className="relative">
                    <img src={c.url.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + c.url : c.url} alt={c.name} loading="lazy"
                      className="w-full aspect-[4/5] object-contain bg-black cursor-zoom-in" title="Tap to zoom"
                      onClick={() => zoomCard(c.url.startsWith("/api/") ? process.env.REACT_APP_BACKEND_URL + c.url : c.url, c.name)} />
                    {c.one_of_one && <span className="absolute top-1 left-1 bg-black/85 border border-amber-400/70 text-amber-400 text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5">1 of 1</span>}
                    {c.bounty > 0 && <span className="absolute top-1 right-1 bg-pb-red text-black text-[8px] font-black uppercase tracking-widest px-1.5 py-0.5">{c.bounty} BLC bounty</span>}
                    {c.edition_total > 0 && <span className="absolute bottom-1 left-1 bg-black/85 border border-[#A3E635]/60 text-[#A3E635] text-[8px] font-black uppercase px-1.5 py-0.5" data-testid={`pool-edition-${i}`}>Numbered /{c.edition_total} · {c.edition_left} left</span>}
                    {c.copies > 1 && <span className="absolute bottom-1 right-1 bg-black/80 text-white text-[9px] font-bold px-1.5 py-0.5">×{c.copies}</span>}
                  </div>
                  <div className="mt-1.5 flex items-center justify-between gap-1">
                    <span className="text-[11px] font-bold truncate">{c.name}</span>
                    <span className={`text-[8px] font-black uppercase tracking-widest shrink-0 ${
                      c.rarity === "exclusive" ? "text-pb-red" : c.rarity === "icon" ? "text-white" : c.rarity === "legendary" ? "text-violet-300" : c.rarity === "gold" ? "text-yellow-400" : c.rarity === "holo" ? "text-fuchsia-300" : c.rarity === "gem" ? "text-sky-300" : "text-zinc-500"}`}>
                      {c.gem_label || c.rarity}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ) : market.length === 0 ? (
        <div className="text-sm text-zinc-500 border border-pb-border bg-pb-surface p-8 text-center" data-testid="market-empty">
          Nothing listed yet — be the first to flip a card from your collection.
        </div>
      ) : (() => {
        const priceOk = (c) => (!mkMin || c.price >= +mkMin) && (!mkMax || c.price <= +mkMax);
        const rarOk = (c) => mkRarity === "all" || (mkRarity === "1of1" ? c.one_of_one : c.rarity === mkRarity);
        const gradeOk = (c) => mkGrade === "all" || (mkGrade === "slabbed" ? c.slabbed : mkGrade === "raw" ? !c.grade : (c.grade || 0) >= +mkGrade);
        const mkt = market.filter((c) => priceOk(c) && rarOk(c) && gradeOk(c));
        const signed = mkt.filter((c) => c.autographed);
        const houseDrops = mkt.filter((c) => !c.autographed && (c.source === "house_reserve" || c.source === "forge_market"));
        const community = mkt.filter((c) => !signed.includes(c) && !houseDrops.includes(c));
        const filtersOn = mkRarity !== "all" || mkGrade !== "all" || mkMin || mkMax;
        const tile = (c) => (
          <CardTile key={c.id} c={c} testId={`market-${c.id}`}
            action={c.owner_id === user?.id ? "Your Listing" : `Buy · ${c.price} BLC`}
            onAction={(x) => (x.owner_id === user?.id ? (setListing(x), setListPrice(x.price || "")) : buy(x))} />
        );
        return (
          <>
            {market.length > 0 && (
              <div className="border border-pb-border/70 bg-black/50 backdrop-blur-sm p-3 mb-5 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid="market-filters">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-500">Rarity</span>
                  {[["all", "All"], ["standard", "Standard"], ["common", "Common"], ["rare", "Rare"], ["holo", "Holo"], ["gold", "Gold"], ["gem", "Gem"], ["elite", "Elite"], ["legendary", "Legendary"], ["icon", "Icon"], ["exclusive", "Exclusive"], ["1of1", "1/1"]].map(([v, lbl]) => (
                    <button key={v} data-testid={`mkf-rarity-${v}`} onClick={() => { setMkRarity(v); setMarketPage(0); }}
                      className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${mkRarity === v ? "bg-pb-cyan text-black border-pb-cyan" : "border-pb-border text-zinc-400 hover:border-pb-cyan/60"}`}>
                      {lbl}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-500">Grade</span>
                  <select data-testid="mkf-grade" value={mkGrade} onChange={(e) => { setMkGrade(e.target.value); setMarketPage(0); }}
                    className="bg-pb-elevated border border-pb-border px-2 py-1.5 text-[10px] uppercase font-bold text-white focus:outline-none focus:border-pb-cyan">
                    <option value="all">All</option>
                    <option value="raw">Raw (ungraded)</option>
                    <option value="8">Graded 8+</option>
                    <option value="9">Graded 9+</option>
                    <option value="10">Graded 10</option>
                    <option value="slabbed">Slabbed</option>
                  </select>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] font-black uppercase tracking-[0.25em] text-zinc-500">Price BLC</span>
                  <input data-testid="mkf-price-min" type="number" min="0" value={mkMin} onChange={(e) => { setMkMin(e.target.value); setMarketPage(0); }} placeholder="Min"
                    className="w-16 bg-pb-elevated border border-pb-border px-2 py-1.5 text-[10px] text-white focus:outline-none focus:border-pb-cyan" />
                  <span className="text-zinc-600 text-xs">—</span>
                  <input data-testid="mkf-price-max" type="number" min="0" value={mkMax} onChange={(e) => { setMkMax(e.target.value); setMarketPage(0); }} placeholder="Max"
                    className="w-16 bg-pb-elevated border border-pb-border px-2 py-1.5 text-[10px] text-white focus:outline-none focus:border-pb-cyan" />
                </div>
                {filtersOn && (
                  <button data-testid="mkf-clear" onClick={() => { setMkRarity("all"); setMkGrade("all"); setMkMin(""); setMkMax(""); setMarketPage(0); }}
                    className="ml-auto text-[9px] font-black uppercase tracking-widest border border-pb-red/60 text-pb-red px-2.5 py-1.5 hover:bg-pb-red/10 transition-colors">
                    Clear · {mkt.length} match{mkt.length === 1 ? "" : "es"}
                  </button>
                )}
              </div>
            )}
            {filtersOn && mkt.length === 0 && (
              <div className="border border-dashed border-pb-border p-8 text-center text-sm text-zinc-500 mb-5" data-testid="market-filter-empty">
                No cards match those filters — loosen them up or clear to see the full market.
              </div>
            )}
            {signed.length > 0 && (
              <div className="border border-yellow-400/60 bg-gradient-to-r from-yellow-500/10 via-pb-surface to-pb-surface p-4 mb-5" data-testid="signed-cards-shelf">
                <div className="flex items-center gap-2 mb-1">
                  <PenLine className="w-4 h-4 text-yellow-300" />
                  <span className="font-display font-bold text-lg uppercase">Signed Cards</span>
                  <span className="text-[8px] font-black uppercase tracking-[0.25em] border border-yellow-400 text-yellow-300 px-1.5 py-0.5">Verified Fan Signed</span>
                </div>
                <p className="text-xs text-zinc-500 mb-3">Athlete-signed in person, verified and baked in forever — each one signed once, locked forever. True one-of-a-kinds.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {signed.map(tile)}
                </div>
              </div>
            )}
            {houseDrops.length > 0 && (
              <div className="border border-yellow-500/50 bg-yellow-500/5 p-4 mb-5" data-testid="house-drops-shelf">
                <div className="flex items-center gap-2 mb-1">
                  <Crown className="w-4 h-4 text-yellow-400" />
                  <span className="font-display font-bold text-lg uppercase">House Drops</span>
                  <span className="text-[8px] font-black uppercase tracking-[0.25em] border border-yellow-500 text-yellow-400 px-1.5 py-0.5">Sold by the Bullpen</span>
                </div>
                <p className="text-xs text-zinc-500 mb-3">Official Bullpen-held cards — verified provenance straight from the house vault.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {houseDrops.map(tile)}
                </div>
              </div>
            )}
            {community.length > 0 && (() => {
              const PER = 10;
              const totalPages = Math.ceil(community.length / PER);
              const page = Math.min(marketPage, totalPages - 1);
              const shown = community.slice(page * PER, page * PER + PER);
              return (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3" data-testid="vault-market">
                    {shown.map(tile)}
                  </div>
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-3 mt-6" data-testid="market-pagination">
                      <button data-testid="market-prev-page" disabled={page === 0}
                        onClick={() => { setMarketPage(page - 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className="border border-pb-border text-zinc-400 text-[10px] font-black uppercase tracking-widest px-4 py-2.5 hover:border-pb-cyan hover:text-pb-cyan transition-colors disabled:opacity-30">
                        ← Prev
                      </button>
                      <span className="text-[10px] font-black uppercase tracking-widest text-zinc-500" data-testid="market-page-info">
                        Page {page + 1} of {totalPages} · {community.length} cards
                      </span>
                      <button data-testid="market-next-page" disabled={page >= totalPages - 1}
                        onClick={() => { setMarketPage(page + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                        className="bg-pb-cyan text-black text-[10px] font-black uppercase tracking-widest px-5 py-2.5 hover:opacity-90 active:scale-95 transition-all disabled:opacity-30">
                        Next Page →
                      </button>
                    </div>
                  )}
                </>
              );
            })()}
          </>
        );
      })()}
      </div>

      {/* Modals below live OUTSIDE the bg z-wrapper so they stack above the sticky header */}
      {showcaseOpen && (
        <div className="fixed inset-0 z-[85] bg-black/90 overflow-y-auto overscroll-contain p-4 sm:p-8" onClick={() => setShowcaseOpen(false)} data-testid="showcase-modal">
          <div className="max-w-5xl mx-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#A3E635]">The Showcase Window</div>
                <h2 className="font-display font-black uppercase text-3xl">On Display</h2>
              </div>
              <button data-testid="showcase-close" onClick={() => setShowcaseOpen(false)}
                className="w-9 h-9 flex items-center justify-center border border-pb-border text-zinc-400 hover:text-white hover:border-white transition-colors"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs text-zinc-500 mb-6">Hand-picked by the Bullpen — cards live in packs right now, sneak peeks of what's being forged, and grails worth chasing.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {showcase.map((c) => <ShowcaseCard key={c.id} c={c} />)}
            </div>
            <div className="text-center mt-8">
              <button data-testid="showcase-rip-cta" onClick={() => { setShowcaseOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                className="bg-pb-red text-black font-black uppercase tracking-widest px-8 py-3 text-xs hover:bg-[#BEF264] transition-colors">
                Rip a pack & chase these →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pack reveal modal */}
      {slabbing && <SlabCeremony c={slabbing} fee={cfg.slab_fee_blc ?? 15} onClose={(changed) => { setSlabbing(null); if (changed) load(); }} />}

      {signing && (
        <AutographModal copy={signing} proName={signable[signing.card_id]?.pro_name}
          onClose={() => setSigning(null)} onSigned={() => load()} />
      )}
      {packResult && (() => {
        const allFlipped = packResult.cards.every((_, i) => flipped[i]);
        const bountyRevealed = packResult.cards.some((c, i) => flipped[i] && c.bounty_won > 0);
        return (
          <div className="fixed inset-0 z-[90] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
            {flash && <div className="bounty-flash z-[95]" data-testid="bounty-flash" />}
            <div className="max-w-2xl w-full my-8" data-testid="pack-reveal">
              <div className="text-center mb-5">
                <div className="font-display font-black text-3xl uppercase">Pack Ripped!</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mt-1">
                  {allFlipped ? "Your pulls" : "Tap each card to reveal it"}
                </div>
                {bountyRevealed && (
                  <div className="bounty-banner-in mt-3 border border-yellow-400 bg-yellow-400/15 text-yellow-300 font-display font-black text-xl uppercase px-4 py-3" data-testid="bounty-win-banner">
                    <Trophy className="w-5 h-5 inline mr-2 -mt-1" />
                    BOUNTY PULLED! +{packResult.bounty_wins.reduce((s, b) => s + b.prize, 0)} BLC
                    {packResult.bounty_wins.filter((b) => b.perks).map((b, i) => (
                      <div key={i} className="flex items-center justify-center gap-1.5 text-xs font-bold normal-case tracking-normal text-amber-200 mt-1.5" data-testid="bounty-win-perks">
                        <Gift className="w-3.5 h-3.5" /> Also comes with: {b.perks}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                {packResult.cards.map((c, i) => (
                  <div key={c.id} data-testid={`pack-slot-${i}`} onClick={() => flip(i)}
                    className="card-deal flip-card cursor-pointer" style={{ animationDelay: `${i * 130}ms` }}>
                    <div className={`flip-inner ${flipped[i] ? "is-flipped" : ""}`}>
                      <div className="flip-front border border-pb-border bg-gradient-to-br from-[#161616] via-[#0c0c0c] to-[#161616] flex flex-col items-center justify-center gap-2.5 hover:border-pb-red transition-colors">
                        <Layers className="w-7 h-7 text-pb-red/70" />
                        <span className="font-display font-black text-[9px] tracking-[0.3em] text-zinc-600 uppercase text-center px-2">Pickle<br />Bullpen</span>
                      </div>
                      <div className={`flip-back ${flipped[i] ? (c.bounty_won > 0 ? "glow-bounty" : c.rarity === "exclusive" ? "glow-exclusive" : c.rarity === "gold" ? "glow-gold" : c.rarity === "holo" ? "glow-holo" : "") : ""}`}>
                        <CardTile c={c} testId={`pulled-${c.id}`} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {allFlipped && (
                <div className="text-center text-xs text-zinc-500 mt-4 animate-fade-up">Balance: {packResult.balance} BLC · Bonus: {packResult.bonus_balance} BLC</div>
              )}
              <div className="flex justify-center gap-2 mt-4">
                {!allFlipped && (
                  <button data-testid="pack-flip-all" onClick={flipAll}
                    className="flex items-center gap-2 border border-pb-red text-pb-red font-black uppercase tracking-widest px-6 py-2.5 text-xs hover:bg-pb-red hover:text-black transition-colors">
                    Flip All
                  </button>
                )}
                <button data-testid="pack-reveal-close" onClick={() => setPackResult(null)}
                  className={`flex items-center gap-2 font-black uppercase tracking-widest px-6 py-2.5 text-xs transition-colors ${allFlipped ? "bg-pb-red text-black hover:bg-[#BEF264]" : "border border-pb-border text-zinc-400 hover:text-white"}`}>
                  Into the Vault
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* List for sale modal */}
      {listing && (
        <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4" onClick={() => setListing(null)}>
          <div className="bg-pb-surface border border-pb-border p-6 w-full max-w-xs" onClick={(e) => e.stopPropagation()} data-testid="list-modal">
            <div className="flex items-center justify-between mb-3">
              <span className="font-display font-bold uppercase">Sell {listing.subject_name}'s card</span>
              <button onClick={() => setListing(null)} data-testid="list-modal-close"><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
            </div>
            <img src={listing.url} alt="" className="w-28 mx-auto mb-3 border border-pb-border" />
            <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Asking price (0 to unlist)</label>
            <div className="flex items-center bg-pb-elevated border border-pb-border focus-within:border-pb-cyan mb-3">
              <input data-testid="list-price-input" type="number" min={0} step="0.5" value={listPrice}
                onChange={(e) => setListPrice(e.target.value)}
                className="w-full bg-transparent px-3 py-2.5 text-white font-mono focus:outline-none" />
              <span className="pr-3 text-[10px] text-zinc-500 font-bold">BLC</span>
            </div>
            <p className="text-[10px] text-zinc-600 mb-3">House fee {cfg.market_fee_pct}% + {cfg.royalty_pct}% royalty to {listing.subject_name} come out of the sale.</p>
            <button data-testid="list-submit-btn" onClick={submitList} disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-pb-cyan text-black font-black uppercase tracking-widest py-2.5 text-xs hover:bg-[#BEF264] disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Tag className="w-3.5 h-3.5" />}
              {Number(listPrice) > 0 ? `List for ${listPrice} BLC` : "Remove Listing"}
            </button>
          </div>
        </div>
      )}

      {/* Gift modal */}
      {gifting && (
        <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4" onClick={() => setGifting(null)}>
          <div className="bg-pb-surface border border-pb-border p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()} data-testid="gift-modal">
            <div className="flex items-center justify-between mb-3">
              <span className="font-display font-bold uppercase flex items-center gap-2"><Send className="w-4 h-4 text-pb-cyan" /> Send this card</span>
              <button onClick={() => setGifting(null)} data-testid="gift-modal-close"><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
            </div>
            <img src={gifting.url} alt="" className="w-24 mx-auto mb-3 border border-pb-border" />
            <p className="text-[10px] text-zinc-500 mb-3 text-center">"{gifting.subject_name}" moves to their vault permanently — the transfer is stamped into the card's public registry.</p>
            {gifting.price > 0 && <p className="text-xs text-amber-400 mb-3 text-center" data-testid="gift-listed-warning">This card is listed on the marketplace — remove the listing first.</p>}
            {!recip ? (
              <>
                <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">Search player by name</label>
                <input data-testid="gift-recipient-search" value={recipQ} onChange={(e) => setRecipQ(e.target.value)} placeholder="Type at least 2 letters…"
                  className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-white text-sm focus:outline-none focus:border-pb-cyan mb-2" />
                <div className="max-h-40 overflow-y-auto divide-y divide-pb-border/40">
                  {recips.map((r) => (
                    <button key={r.id} data-testid={`gift-recipient-${r.id}`} onClick={() => setRecip(r)}
                      className="w-full flex items-center gap-2.5 px-2 py-2 text-left hover:bg-pb-elevated transition-colors">
                      {r.avatar ? <img src={r.avatar} alt="" className="w-6 h-6 rounded-full object-cover border border-zinc-700" />
                        : <span className="w-6 h-6 rounded-full bg-pb-cyan/20 border border-pb-cyan/40 flex items-center justify-center text-[9px] font-black text-pb-cyan">{(r.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>}
                      <span className="text-sm font-semibold truncate">{r.name}</span>
                    </button>
                  ))}
                  {recipQ.trim().length >= 2 && recips.length === 0 && <div className="text-xs text-zinc-600 py-2 px-2">No players found.</div>}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between border border-pb-cyan/40 bg-pb-cyan/5 px-3 py-2.5 mb-3" data-testid="gift-recipient-selected">
                  <span className="text-sm font-semibold">{recip.name}</span>
                  <button onClick={() => setRecip(null)} data-testid="gift-recipient-change" className="text-[9px] uppercase tracking-widest text-zinc-500 hover:text-white">Change</button>
                </div>
                {giftFee > 0 && <p className="text-[10px] text-zinc-500 mb-3" data-testid="gift-fee-note">Transfer fee: <span className="text-pb-cyan font-bold">{giftFee} BLC</span> — deducted from your balance.</p>}
                <button data-testid="gift-send-btn" onClick={sendGift} disabled={giftBusy || gifting.price > 0}
                  className="w-full flex items-center justify-center gap-2 bg-pb-cyan text-black font-black uppercase tracking-widest py-2.5 text-xs hover:bg-[#BEF264] disabled:opacity-50">
                  {giftBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  Send card{giftFee > 0 ? ` · ${giftFee} BLC` : ""}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Registry / QR modal */}
      {qrCard && (
        <div className="fixed inset-0 z-[90] bg-black/80 flex items-center justify-center p-4" onClick={() => setQrCard(null)}>
          <div className="bg-pb-surface border border-pb-border p-6 w-full max-w-xs text-center" onClick={(e) => e.stopPropagation()} data-testid="qr-modal">
            <div className="flex items-center justify-between mb-3">
              <span className="font-display font-bold uppercase flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-400" /> Card Registry</span>
              <button onClick={() => setQrCard(null)} data-testid="qr-modal-close"><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
            </div>
            {!registry ? (
              <div className="py-10"><Loader2 className="w-6 h-6 text-pb-cyan animate-spin mx-auto" /></div>
            ) : (
              <>
                <div className="bg-white p-3 inline-block mb-3">
                  <QRCodeSVG value={`${window.location.origin}/card/verify/${registry.serial}`} size={150} data-testid="card-qr" />
                </div>
                <div className="font-mono font-black text-xl text-pb-cyan tracking-widest" data-testid="card-serial">{registry.serial}</div>
                <div className="text-[9px] uppercase tracking-widest text-zinc-500 mb-3">Auth code {registry.auth_code}</div>
                <p className="text-[10px] text-zinc-500 mb-4">This copy is protected like currency — printed serial, micro-text, a unique security pattern and an invisible digital watermark are embedded in its image. Anyone can scan this code to confirm it's genuine, see the registered owner and its full transfer history.</p>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="qr-copy-link" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/card/verify/${registry.serial}`); toast.success("Verification link copied"); }}
                    className="flex items-center justify-center gap-1.5 border border-pb-border text-zinc-300 text-[9px] font-black uppercase tracking-widest py-2 hover:border-pb-cyan hover:text-pb-cyan transition-colors">
                    <Copy className="w-3 h-3" /> Copy Link
                  </button>
                  <a data-testid="qr-open-page" href={`/card/verify/${registry.serial}`} target="_blank" rel="noreferrer"
                    className="flex items-center justify-center gap-1.5 bg-pb-cyan text-black text-[9px] font-black uppercase tracking-widest py-2 hover:bg-[#BEF264] transition-colors">
                    <ExternalLink className="w-3 h-3" /> Open Page
                  </a>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
